import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  TrendingUp, CreditCard, FileText, Users, QrCode, Edit,
  ArrowRight, Wifi
} from 'lucide-react'
import Card from '../../components/ui/Card'
import Badge from '../../components/ui/Badge'
import Button from '../../components/ui/Button'
import Avatar from '../../components/ui/Avatar'


export default function Overview() {
  const [period, setPeriod] = useState('month')

  const stats = [
    {
      label: 'Scans ce mois-ci',
      value: '3 842',
      change: '+24.8%',
      icon: TrendingUp,
      color: 'text-teal',
      iconBg: 'bg-teal-50',
      extra: 'vs 3 078 scans le mois précédent',
    },
    {
      label: 'Cartes actives',
      value: '4 / 5',
      change: 'Quota 80%',
      icon: CreditCard,
      color: 'text-navy',
      iconBg: 'bg-navy-50',
      extra: 'PVC Mat (x2), Bois Bambou, Métal',
    },
    {
      label: 'Pages publiées',
      value: '3',
      change: 'en ligne',
      icon: FileText,
      color: 'text-gold',
      iconBg: 'bg-gold-50',
      extra: '1 Carte Pro · 1 Bio Link · 1 Menu Démo',
    },
    {
      label: 'Leads capturés',
      value: '148',
      change: '+18 sem.',
      icon: Users,
      color: 'text-teal',
      iconBg: 'bg-teal-50',
      extra: 'Taux de conversion contact : 3,8%',
    },
  ]

  const recentPages = [
    {
      id: 1,
      title: 'Alexandre Martin — Direction Artistique',
      slug: 'alexandre-martin',
      views: 2410,
      status: 'published',
      avatar: '👨‍💼',
    },
    {
      id: 2,
      title: 'Studio Design Paris — Portfolio & Projets',
      slug: 'studiodesignparis',
      views: 1190,
      status: 'published',
      avatar: '🎨',
    },
    {
      id: 3,
      title: 'Carte des Cocktails & Vins — Hôtel',
      slug: 'saint-roch-bar',
      views: 242,
      status: 'draft',
      avatar: '🍸',
    },
  ]

  const recentLeads = [
    {
      id: 1,
      name: 'Sophie Delaunay',
      role: 'VP Marketing @ LVMH Digital',
      time: "Aujourd'hui 11:42",
      source: 'vCard échangée',
      initials: 'SD',
    },
    {
      id: 2,
      name: 'Marc Benamou',
      role: "Directeur d'agence @ Koto Paris",
      time: 'Hier 17:15',
      source: 'Email validé',
      initials: 'MB',
    },
    {
      id: 3,
      name: 'Camille Laurent',
      role: 'Fondatrice @ Maison Sélène',
      time: '12 Mai 09:30',
      source: 'Lien Bio cliqué',
      initials: 'CL',
    },
    {
      id: 4,
      name: 'Yassine Rahmani',
      role: "Architecte d'intérieur @ Casablanca",
      time: '10 Mai 16:04',
      source: 'vCard enregistrée',
      initials: 'YR',
    },
  ]

  return (
    <div className="space-y-6">
      {/* ===== PRO BANNER ===== */}
      <Card className="p-5 border-gold-100 bg-gradient-to-r from-gold-50 via-gold-50/50 to-white">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-gold-100 flex items-center justify-center flex-shrink-0">
            <span className="text-2xl">✨</span>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-bold text-gold-700 uppercase tracking-wider">
                Offre Pro
              </span>
            </div>
            <h2 className="font-bold text-navy text-lg mb-1">
              Passez à Tapio Premium pour des cartes illimitées et analytics avancés
            </h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              Débloquez le domaine personnalisé, les intégrations CRM HubSpot/Salesforce,
              les exports CSV illimités et la commande de cartes métal gravées au laser
              sans frais techniques.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button size="sm" icon={ArrowRight}>
              Passer à Premium (19€ / mois)
            </Button>
            <Button variant="outline" size="sm">
              En savoir plus
            </Button>
          </div>
        </div>
      </Card>

      {/* ===== STATS ===== */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, i) => (
          <Card key={i} className="p-5" hover>
            <div className="flex items-start justify-between mb-3">
              <div className={`p-2.5 rounded-xl ${stat.iconBg} ${stat.color}`}>
                <stat.icon className="w-5 h-5" />
              </div>
              <Badge variant="success">{stat.change}</Badge>
            </div>
            <p className="text-xs text-gray-500 mb-1">{stat.label}</p>
            <p className="text-2xl font-bold text-navy mb-2">{stat.value}</p>
            {stat.extra && (
              <p className="text-[10px] text-gray-400 leading-tight">{stat.extra}</p>
            )}
          </Card>
        ))}
      </div>

      {/* ===== ACTIVITÉ GRAPHIQUE ===== */}
      <Card className="p-6">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-lg font-bold text-navy">
                Activité des interactions & Scans
              </h2>
              <Badge variant="gray" className="text-[10px]">
                Temps réel
              </Badge>
            </div>
            <p className="text-sm text-gray-500">
              Flux des scans physiques NFC et requêtes QR code consolidés sur la période.
            </p>
          </div>
          <div className="flex items-center gap-1 bg-graysoft rounded-xl p-1">
            {[
              { key: '7d', label: '7j' },
              { key: 'month', label: 'Ce mois' },
              { key: '30d', label: '30j' },
              { key: '1y', label: '1 an' },
            ].map((p) => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  period === p.key
                    ? 'bg-navy text-white shadow-sm'
                    : 'text-gray-500 hover:bg-white'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Légende */}
        <div className="flex items-center gap-5 mb-4 text-xs">
          <span className="flex items-center gap-1.5 text-gray-600">
            <span className="w-2 h-2 rounded-full bg-teal" />
            NFC Tap (72%)
          </span>
          <span className="flex items-center gap-1.5 text-gray-600">
            <span className="w-2 h-2 rounded-full bg-navy" />
            Scan QR (24%)
          </span>
          <span className="flex items-center gap-1.5 text-gray-600">
            <span className="w-2 h-2 rounded-full bg-gray-300" />
            Lien direct (4%)
          </span>
        </div>

        {/* Graphique (placeholder visuel) */}
        <div className="relative h-72 bg-gradient-to-b from-teal-50/50 to-transparent rounded-xl border border-gray-100 overflow-hidden">
          {/* Grille */}
          <div className="absolute inset-0 flex flex-col justify-between p-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="border-t border-gray-100" />
            ))}
          </div>

          {/* Ligne simulée SVG */}
          <svg
            className="absolute inset-0 w-full h-full"
            viewBox="0 0 800 300"
            preserveAspectRatio="none"
          >
            {/* Ligne teal (NFC) */}
            <path
              d="M 0 220 Q 100 200, 200 180 T 400 130 T 600 90 T 800 60"
              fill="none"
              stroke="#14B8A6"
              strokeWidth="3"
            />
            {/* Ligne navy (QR) */}
            <path
              d="M 0 260 Q 200 240, 400 220 T 800 180"
              fill="none"
              stroke="#0F1B3D"
              strokeWidth="2"
              strokeDasharray="6 4"
            />
          </svg>

          {/* Tooltip */}
          <div className="absolute top-1/3 right-1/4 bg-navy text-white rounded-lg px-3 py-2 shadow-lg">
            <p className="text-[10px] opacity-70 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-teal" />
              Salon Tech Paris 2025
            </p>
            <p className="font-bold text-sm">420 scans physiques</p>
            <p className="text-[10px] opacity-70">jeudi 14 Mai · 14h – 18h</p>
          </div>

          {/* Axe X */}
          <div className="absolute bottom-0 left-0 right-0 flex justify-between px-6 pb-3 text-[10px] text-gray-400">
            {['01 Mai', '06 Mai', '11 Mai', '14 Mai', '19 Mai', '24 Mai', '31 Mai'].map(
              (d, i) => (
                <span key={i}>{d}</span>
              )
            )}
          </div>
        </div>
      </Card>

      {/* ===== 2 COLONNES ===== */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Mes Pages Récentes */}
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-bold text-navy">Mes Pages Récentes</h2>
              <Badge variant="gray" className="text-[10px]">
                3 actives
              </Badge>
            </div>
            <Link
              to="/app/pages"
              className="text-xs text-teal font-semibold hover:underline flex items-center gap-1"
            >
              Gérer toutes <ArrowRight className="w-3 h-3" />
            </Link>
          </div>

          <div className="space-y-2">
            {recentPages.map((page) => (
              <Card key={page.id} className="p-4" hover>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-navy to-navy-700 flex items-center justify-center text-2xl flex-shrink-0">
                    {page.avatar}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <h3 className="font-semibold text-navy text-sm truncate">
                        {page.title}
                      </h3>
                      <Badge
                        variant={page.status === 'published' ? 'success' : 'warning'}
                        dot
                        className="text-[10px]"
                      >
                        {page.status === 'published' ? 'Publiée' : 'Brouillon'}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-500 truncate">
                      tapio.link/{page.slug}
                    </p>
                    <p className="text-[10px] text-gray-400 mt-1">
                      {page.views.toLocaleString('fr-FR')} visites
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm">
                      <QrCode className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" className="text-xs">
                      <Edit className="w-3 h-3" />
                      Éditer
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Colonne droite */}
        <div className="space-y-6">
          {/* Commande en cours */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-navy text-sm">Commande en cours</h3>
              <Link
                to="/app/orders"
                className="text-[10px] text-teal font-semibold hover:underline"
              >
                Suivi Colissimo
              </Link>
            </div>
            <Card className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-navy flex items-center justify-center flex-shrink-0">
                  <span className="text-teal font-bold text-sm">📦</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-navy truncate">
                    Carte Métal Noir Mat Gravée
                  </p>
                  <p className="text-[10px] text-gray-500">
                    Commande #TAP-9241 · 1 exemplaire
                  </p>
                </div>
                <Badge variant="info" className="text-[10px]">
                  En transit
                </Badge>
              </div>
              <div className="h-1.5 bg-graysoft rounded-full overflow-hidden mb-2">
                <div className="h-full w-2/3 bg-teal rounded-full" />
              </div>
              <div className="flex items-center justify-between text-[10px] text-gray-500">
                <span>📦 Colissimo 6A2049182390</span>
                <span className="text-navy font-semibold">
                  Livraison prévue demain
                </span>
              </div>
            </Card>
          </div>

          {/* Derniers contacts */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-navy text-sm">
                Derniers Contacts Capturés
              </h3>
              <Link
                to="/app/leads"
                className="text-[10px] text-teal font-semibold hover:underline"
              >
                Exporter .CSV
              </Link>
            </div>
            <Card className="p-3">
              <div className="divide-y divide-gray-100">
                {recentLeads.map((lead) => (
                  <div key={lead.id} className="flex items-center gap-3 py-2.5">
                    <Avatar name={lead.name} size="sm" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-navy truncate">
                        {lead.name}
                      </p>
                      <p className="text-[10px] text-gray-500 truncate">{lead.role}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-[10px] text-gray-400">{lead.time}</p>
                      <p className="text-[9px] text-teal font-semibold">
                        {lead.source}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <Link to="/app/leads">
                <Button variant="outline" size="sm" className="w-full mt-2 text-xs">
                  Voir les 148 contacts →
                </Button>
              </Link>
            </Card>
          </div>
        </div>
      </div>

      {/* ===== BESOIN DE SYNCHRONISER ===== */}
      <Card className="p-5 bg-gradient-to-r from-navy-50 to-white border-navy-100">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-navy flex items-center justify-center flex-shrink-0">
            <Wifi className="w-5 h-5 text-teal" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-navy mb-1 text-sm">
              Besoin de synchroniser une carte physique ?
            </h3>
            <p className="text-xs text-gray-600 leading-relaxed">
              Associez instantanément une nouvelle carte NFC vierge à n'importe quelle
              page depuis l'application mobile ou via l'onglet{' '}
              <Link to="/app/cards" className="text-teal font-semibold hover:underline">
                Mes Cartes NFC
              </Link>
              .
            </p>
          </div>
          <Button size="sm" variant="outline" className="flex-shrink-0">
            Synchroniser
          </Button>
        </div>
      </Card>
    </div>
  )
}